/* Subscription price calculator — plan, employees, cycle, AI pack → subtotal / VAT 15% / total.
 * All amounts come from domain `subAmount` (the same function the API uses for invoices). */
import { useId } from "react";
import type { Cycle } from "../../../data/types";
import { AI_PACK_PRICE } from "../../../domain/compliance";
import { useI18n } from "../../../i18n";
import { Field, Input, Money, Seg } from "../../../ui";
import { PLANS, perEmployee, quote, type PlanName } from "./pricingData";

export interface CalcValue { plan: PlanName; cycle: Cycle; seats: number | ""; ai: boolean }

export const seatsError = (seats: number | "", L: (en: string, ar: string) => string): string | undefined => {
  if (seats === "" || !Number.isInteger(seats) || seats < 1) return L("Enter at least 1 employee", "أدخل موظفًا واحدًا على الأقل");
  if (seats > 1000) return L("Up to 1,000 employees — contact us for more", "حتى 1,000 موظف — تواصل معنا للمزيد");
  return undefined;
};

export function PriceCalculator({ prices, value, onChange, dark }: { prices: Record<string, number>; value: CalcValue; onChange: (v: CalcValue) => void; dark?: boolean }) {
  const { t, L } = useI18n();
  const seatsId = useId();
  const aiId = useId();
  const err = seatsError(value.seats, L);
  const seats = typeof value.seats === "number" ? value.seats : 0;
  const q = quote(prices, value.plan, value.cycle, err ? 1 : seats, value.ai ? 1 : 0);
  const set = (p: Partial<CalcValue>) => onChange({ ...value, ...p });
  const rowCls = dark ? "co-row" : "row between sm";
  const rowSt = dark ? undefined : { padding: "8px 0", borderTop: "1px dashed var(--border)" };
  return (
    <div className="g g-2" data-testid="price-calc" style={{ alignItems: "start" }}>
      <div className="calc-in">
        <Field label={L("Plan", "الباقة")}>
          <Seg label={L("Plan", "الباقة")} items={PLANS.map((p) => [p, p + " · " + perEmployee(prices[p] ?? 0, value.cycle)] as [PlanName, string])} value={value.plan} onChange={(plan) => set({ plan })} />
        </Field>
        <Field label={L("Employees", "عدد الموظفين")} htmlFor={seatsId} err={err}>
          <Input id={seatsId} className="num" type="number" min={1} max={1000} value={value.seats} bad={!!err} data-testid="calc-seats"
            onChange={(e) => set({ seats: e.target.value === "" ? "" : Number(e.target.value) })} />
        </Field>
        <Field label={L("Billing cycle", "دورة الفوترة")}>
          <Seg label={L("Billing cycle", "دورة الفوترة")} items={[["m", t("Monthly")], ["y", t("Annually") + " −20%"]]} value={value.cycle} onChange={(cycle) => set({ cycle })} />
        </Field>
        <label className="check" htmlFor={aiId}>
          <input id={aiId} type="checkbox" checked={value.ai} onChange={(e) => set({ ai: e.target.checked })} data-testid="calc-ai" />{" "}
          {L(`Add AI credits pack (+1,000 credits/month · SAR ${AI_PACK_PRICE})`, `إضافة باقة رصيد الذكاء الاصطناعي (+1,000 رصيد شهريًا · ${AI_PACK_PRICE} ر.س)`)}
        </label>
      </div>
      <div className={dark ? "calc-out" : "card card-b"} aria-live="polite">
        <div className={dark ? "co-big" : "stack"} style={dark ? undefined : { gap: 4, marginBottom: 12 }}>
          <span className={dark ? undefined : "sm muted"}>{value.cycle === "y" ? L("Total per year, incl. VAT", "الإجمالي سنويًا شاملًا الضريبة") : L("Total per month, incl. VAT", "الإجمالي شهريًا شاملًا الضريبة")}</span>
          <b className={dark ? "num" : "kpi-v num"} data-testid="calc-total"><Money value={err ? 0 : q.total} /></b>
        </div>
        <div className={rowCls} style={rowSt}><span>{L("Subtotal", "المجموع")}</span><b className="num" data-testid="calc-subtotal"><Money value={err ? 0 : q.amount} /></b></div>
        <div className={rowCls} style={rowSt}><span>{L("VAT 15%", "ضريبة القيمة المضافة 15%")}</span><b className="num" data-testid="calc-vat"><Money value={err ? 0 : q.vat} /></b></div>
        <p className="xs" style={{ margin: "10px 0 0", opacity: 0.75 }}>
          {value.cycle === "y" ? L("Annual billing: 12 months with 20% off. ", "الفوترة السنوية: 12 شهرًا بخصم 20%. ") : ""}
          {L("Pay by bank transfer after your 30-day free trial.", "ادفع بتحويل بنكي بعد التجربة المجانية 30 يومًا.")}
        </p>
      </div>
    </div>
  );
}
